/**
 * QuoteForm — section wrapper around FormRunner.
 * Gated by the quote_forms feature flag (visibleWhen in siteConfig).
 *
 * The FormDefinition is embedded in section props (MVP) or can later
 * be fetched from clients/{clientId}/forms/{formId}.
 *
 * Registered as: quote / quote-form
 */
import React from "react";
import { FormRunner } from "../../../forms/components/FormRunner.js";
import type { FormDefinition } from "../../../forms/schema/formDef.js";
import type { SectionComponentProps } from "../../registry.js";

interface QuoteFormProps {
  headline?: string;
  subheadline?: string;
  /** Embedded FormDefinition (MVP) */
  form?: FormDefinition;
  /** Firestore form doc id — fetched live (deferred) */
  formId?: string;
  /** Override the serviceType select options in the default form */
  serviceOptions?: Array<{ value: string; label: string }>;
  /** When set, calls this Firebase callable on submit instead of writing to Firestore */
  submitCallable?: string;
}

/** Default contact/quote form used when no form definition is provided */
const DEFAULT_FORM: FormDefinition = {
  id: "default-quote",
  clientId: "",
  title: "Request a Quote",
  submitLabel: "Send Request",
  successMessage: "Thanks! We'll review your request and get back to you within 24 hours.",
  active: true,
  steps: [
    {
      id: "step-1",
      title: "Tell us about your project",
      fields: [
        { id: "serviceType", type: "select", label: "Service needed", required: true, placeholder: "Choose a service…", options: [
          { value: "mowing", label: "Lawn Mowing" },
          { value: "cleanup", label: "Seasonal Cleanup" },
          { value: "fertilisation", label: "Fertilisation" },
          { value: "aeration", label: "Aeration" },
          { value: "other", label: "Other / custom" },
        ]},
        { id: "lotSize", type: "select", label: "Approximate lot size", required: true, placeholder: "Select size…", options: [
          { value: "small", label: "Small (under 5,000 sq ft)" },
          { value: "medium", label: "Medium (5,000–15,000 sq ft)" },
          { value: "large", label: "Large (over 15,000 sq ft)" },
        ], pricingImpact: { type: "fixed", amountCents: 0 } },
        { id: "frequency", type: "select", label: "How often?", required: false, placeholder: "Select frequency…", options: [
          { value: "one-time", label: "One time" },
          { value: "weekly", label: "Weekly" },
          { value: "biweekly", label: "Every two weeks" },
          { value: "monthly", label: "Monthly" },
        ]},
        { id: "notes", type: "textarea", label: "Additional details", required: false, placeholder: "Any specific requests, gates to unlock, pets, etc." },
      ],
    },
    {
      id: "step-2",
      title: "Your contact info",
      fields: [
        { id: "name", type: "text", label: "Name", required: true, placeholder: "Full name" },
        { id: "email", type: "email", label: "Email", required: true, placeholder: "your@email.com" },
        { id: "phone", type: "phone", label: "Phone", required: false, placeholder: "(555) 000-0000" },
        { id: "address", type: "text", label: "Service address", required: true, placeholder: "123 Main St, Wapakoneta OH" },
      ],
    },
  ],
};

export function QuoteForm({ props, clientId }: SectionComponentProps) {
  const {
    headline = "Get a Free Quote",
    subheadline = "Tell us about your project and we'll get back to you with a custom quote.",
    form,
    serviceOptions,
    submitCallable,
  } = props as QuoteFormProps;

  // Merge clientId into the embedded form definition
  let formDef: FormDefinition = form
    ? { ...form, clientId }
    : { ...DEFAULT_FORM, clientId };

  // Override serviceType options if provided
  if (serviceOptions && !form) {
    formDef = {
      ...formDef,
      steps: formDef.steps.map((step) => ({
        ...step,
        fields: step.fields.map((field) =>
          field.id === "serviceType" ? { ...field, options: serviceOptions } : field
        ),
      })),
    };
  }

  return (
    <section className="py-20 px-6 bg-muted/20" id="quote">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{headline}</h2>
          {subheadline && (
            <p className="text-muted-foreground text-base leading-relaxed max-w-md mx-auto">{subheadline}</p>
          )}
        </div>
        <FormRunner form={formDef} clientId={clientId} submitCallable={submitCallable} />
      </div>
    </section>
  );
}
